export interface OperationIdentity {
  readonly id: number;
  readonly signal: AbortSignal;
}

/** Only the most recently started top-level operation may commit workflow results. */
export class LatestOperationCoordinator {
  #current: { readonly controller: AbortController; readonly operation: OperationIdentity } | null =
    null;
  #nextId = 0;

  start(): OperationIdentity {
    this.#current?.controller.abort();
    const controller = new AbortController();
    const operation = { id: ++this.#nextId, signal: controller.signal };
    this.#current = { controller, operation };
    return operation;
  }

  isCurrent(id: number): boolean {
    return this.#current?.operation.id === id;
  }

  current(id: number): OperationIdentity | null {
    return this.isCurrent(id) ? (this.#current?.operation ?? null) : null;
  }

  cancel(id: number): boolean {
    if (!this.isCurrent(id) || !this.#current) return false;
    this.#current.controller.abort();
    this.#current = null;
    return true;
  }

  finish(id: number): boolean {
    if (!this.isCurrent(id) || !this.#current) return false;
    this.#current = null;
    return true;
  }

  dispose(): void {
    if (this.#current) this.cancel(this.#current.operation.id);
  }

  get currentId(): number | null {
    return this.#current?.operation.id ?? null;
  }
}
